import React, { useState, useEffect } from 'react';
import './EnhancedReviewList.css';
import { trackAISummaryGenerated, trackFilterUsed } from '../utils/analytics';
import { canEditReview, updateReview } from '../services/reviewsService';

const EnhancedReviewList = ({
  cluster, onClose, onNewReview, onGenerateSummary,
}) => {
  const [reviews, setReviews] = useState([]);
  const [filteredReviews, setFilteredReviews] = useState([]);
  const [sortBy, setSortBy] = useState('newest'); // 'newest', 'oldest', 'rating'
  const [filterBy, setFilterBy] = useState('all'); // 'all', 'recommended', 'not-recommended'
  const [currentIndex, setCurrentIndex] = useState(0);
  const [viewMode, setViewMode] = useState('list'); // 'list', 'swipe'
  const [summary, setSummary] = useState(null);
  const [loadingSummary, setLoadingSummary] = useState(false);
  const [editingReview, setEditingReview] = useState(null);
  const [editingData, setEditingData] = useState({});
  const [savingEdit, setSavingEdit] = useState(false);

  useEffect(() => {
    if (cluster && cluster.reviews) {
      setReviews(cluster.reviews);
      applyFiltersAndSort(cluster.reviews);
    }
  }, [cluster, sortBy, filterBy]);

  const applyFiltersAndSort = (reviewList) => {
    let filtered = [...reviewList];

    // Apply filter
    if (filterBy === 'recommended') {
      filtered = filtered.filter((review) => review.recommend === 'yes');
    } else if (filterBy === 'not-recommended') {
      filtered = filtered.filter((review) => review.recommend === 'no');
    }

    // Apply sort
    if (sortBy === 'newest') {
      filtered.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
    } else if (sortBy === 'oldest') {
      filtered.sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
    } else if (sortBy === 'rating') {
      filtered.sort((a, b) => (b.safetyFeel || 5) - (a.safetyFeel || 5));
    }

    setFilteredReviews(filtered);
    setCurrentIndex(0);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const nextReview = () => {
    setCurrentIndex((prev) => Math.min(prev + 1, filteredReviews.length - 1));
  };

  const prevReview = () => {
    setCurrentIndex((prev) => Math.max(prev - 1, 0));
  };

  const generateSummary = async () => {
    setLoadingSummary(true);
    try {
      const summaryData = await onGenerateSummary(reviews);
      setSummary(summaryData);
      trackAISummaryGenerated(reviews.length);
    } catch (error) {
      console.error('Error generating summary:', error);
    } finally {
      setLoadingSummary(false);
    }
  };

  const startEditingReview = (review) => {
    setEditingReview(review.id);
    setEditingData({ ...review });
  };

  const cancelEditing = () => {
    setEditingReview(null);
    setEditingData({});
  };

  const saveReviewEdit = async () => {
    setSavingEdit(true);
    try {
      await updateReview(editingReview, editingData);
      // Refresh the reviews data
      setEditingReview(null);
      setEditingData({});
      // The parent component should handle refreshing the data
      if (onClose) onClose(); // This will refresh when reopened
    } catch (error) {
      console.error('Error saving review edit:', error);
      alert('Failed to save changes. Please try again.');
    } finally {
      setSavingEdit(false);
    }
  };

  const renderRating = (value, label) => {
    if (!value) return null;
    return (
      <div className="rating-item">
        <span className="rating-label">
          {label}
          :
        </span>
        <span className="rating-value">
          {value}
          /10
        </span>
      </div>
    );
  };

  const renderYesNo = (value, label) => {
    if (!value) return null;
    return (
      <div className="yes-no-item">
        <span className="yes-no-label">
          {label}
          :
        </span>
        <span className={`yes-no-value ${value === 'yes' ? 'yes' : 'no'}`}>
          {value === 'yes' ? 'Yes' : 'No'}
        </span>
      </div>
    );
  };

  const renderText = (value, label) => {
    if (!value) return null;
    return (
      <div className="text-item">
        <span className="text-label">
          {label}
          :
        </span>
        <span className="text-value">{value}</span>
      </div>
    );
  };

  if (!cluster) return null;

  return (
    <div className="enhanced-review-overlay">
      <div className="enhanced-review-container">
        <div className="review-header">
          <div className="header-info">
            <h2>🏢 Building Reviews</h2>
            <div className="cluster-overview">
              <span className="overview-stat">
                {reviews.length}
                {' '}
                total reviews
              </span>
              <span className="overview-stat">
                {Math.round(reviews.filter((r) => r.recommend === 'yes').length / reviews.length * 100)}
                % recommend
              </span>
              <span className="overview-stat">
                Avg rating:
                {' '}
                {Math.round(reviews.reduce((sum, r) => sum + (r.safetyFeel || 5), 0) / reviews.length)}
                /10
              </span>
            </div>
          </div>
          <div className="header-actions">
            <button onClick={generateSummary} disabled={loadingSummary} className="summary-btn">
              {loadingSummary ? '🤖 Generating...' : '🤖 AI Summary'}
            </button>
            <button onClick={onNewReview} className="new-review-btn">
              Add Review
            </button>
            <button onClick={onClose} className="close-btn">
              ×
            </button>
          </div>
        </div>

        {summary && (
          <div className="summary-section">
            <h3>🤖 AI Summary</h3>
            <div className="summary-content">
              <p className="summary-overview">{summary.overview}</p>
              {summary.pros.length > 0 && (
                <div className="summary-pros">
                  <strong>👍 Pros:</strong>
                  <ul>{summary.pros.map((pro, i) => <li key={i}>{pro}</li>)}</ul>
                </div>
              )}
              {summary.cons.length > 0 && (
                <div className="summary-cons">
                  <strong>👎 Cons:</strong>
                  <ul>{summary.cons.map((con, i) => <li key={i}>{con}</li>)}</ul>
                </div>
              )}
              <div className="summary-stats">
                <div>
                  🏠 Safety:
                  {summary.keyStats.avgSafety}
                  /10
                </div>
                <div>
                  🧹 Cleanliness:
                  {summary.keyStats.avgCleanliness}
                  /10
                </div>
                <div>
                  🔊 Noise:
                  {summary.keyStats.noiseLevel}
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="controls-section">
          <div className="filter-controls">
            <div className="control-group">
              <label>Sort by:</label>
              <select
                value={sortBy}
                onChange={(e) => {
                  const newValue = e.target.value;
                  setSortBy(newValue);
                  trackFilterUsed('sort', newValue);
                }}
              >
                <option value="newest">Newest First</option>
                <option value="oldest">Oldest First</option>
                <option value="rating">Highest Rated</option>
              </select>
            </div>

            <div className="control-group">
              <label>Filter:</label>
              <select
                value={filterBy}
                onChange={(e) => {
                  const newValue = e.target.value;
                  setFilterBy(newValue);
                  trackFilterUsed('filter', newValue);
                }}
              >
                <option value="all">
                  All Reviews (
                  {reviews.length}
                  )
                </option>
                <option value="recommended">
                  Recommended (
                  {reviews.filter((r) => r.recommend === 'yes').length}
                  )
                </option>
                <option value="not-recommended">
                  Not Recommended (
                  {reviews.filter((r) => r.recommend === 'no').length}
                  )
                </option>
              </select>
            </div>

            <div className="control-group">
              <label>View:</label>
              <div className="view-toggle">
                <button
                  className={viewMode === 'list' ? 'active' : ''}
                  onClick={() => setViewMode('list')}
                >
                  📋 List
                </button>
                <button
                  className={viewMode === 'swipe' ? 'active' : ''}
                  onClick={() => setViewMode('swipe')}
                >
                  👆 Swipe
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="reviews-content">
          {filteredReviews.length === 0 ? (
            <div className="no-reviews">
              <p>No reviews match your current filters.</p>
              <button onClick={() => { setFilterBy('all'); setSortBy('newest'); }}>
                Show All Reviews
              </button>
            </div>
          ) : viewMode === 'list' ? (
            <div className="reviews-list">
              {filteredReviews.map((review) => (
                <div key={review.id} className="review-card">
                  {editingReview === review.id ? (
                    <EditReviewForm
                      review={editingData}
                      onSave={saveReviewEdit}
                      onCancel={cancelEditing}
                      onChange={setEditingData}
                      saving={savingEdit}
                    />
                  ) : (
                    <>
                      <div className="review-header">
                        <div className="recommendation-badge">
                          <span className={`badge ${review.recommend === 'yes' ? 'recommend' : 'not-recommend'}`}>
                            {review.recommend === 'yes' ? '✅ Recommended' : '❌ Not Recommended'}
                          </span>
                          {canEditReview(review) && (
                            <button
                              onClick={() => startEditingReview(review)}
                              className="edit-review-btn"
                              title="Edit this review (24h limit)"
                            >
                              ✏️ Edit
                            </button>
                          )}
                        </div>
                        <div className="review-date">
                          {formatDate(review.createdAt)}
                          {review.version > 1 && <span className="version-info"> (v{review.version})</span>}
                        </div>
                      </div>

                  {review.recommendNotes && (
                    <div className="recommend-notes">
                      <em>
                        "
                        {review.recommendNotes}
                        "
                      </em>
                    </div>
                  )}

                  <div className="review-details">
                    {/* Key metrics */}
                    <div className="detail-section">
                      <h4>Key Ratings</h4>
                      {renderRating(review.safetyFeel, 'Safety')}
                      {renderRating(review.areaCleanliness, 'Cleanliness')}
                      {renderRating(review.trafficLevel, 'Traffic')}
                      {renderText(review.noiseLevel, 'Noise')}
                    </div>

                    {/* Issues mentioned */}
                    {(review.otherProblems || review.leakyRoofsThefts) && (
                      <div className="detail-section">
                        <h4>Issues Reported</h4>
                        {renderText(review.otherProblems, 'Problems')}
                        {renderYesNo(review.leakyRoofsThefts, 'Leaks/Thefts')}
                      </div>
                    )}
                  </div>
                    </>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="swipe-view">
              <div className="swipe-controls">
                <button onClick={prevReview} disabled={currentIndex === 0}>
                  ‹ Previous
                </button>
                <span className="review-counter">
                  {currentIndex + 1}
                  {' '}
                  of
                  {filteredReviews.length}
                </span>
                <button onClick={nextReview} disabled={currentIndex === filteredReviews.length - 1}>
                  Next ›
                </button>
              </div>

              {filteredReviews[currentIndex] && (
                <div className="review-card detailed">
                  <div className="review-header">
                    <div className="recommendation-badge">
                      <span className={`badge ${filteredReviews[currentIndex].recommend === 'yes' ? 'recommend' : 'not-recommend'}`}>
                        {filteredReviews[currentIndex].recommend === 'yes' ? '✅ Recommended' : '❌ Not Recommended'}
                      </span>
                    </div>
                    <div className="review-date">
                      {formatDate(filteredReviews[currentIndex].createdAt)}
                    </div>
                  </div>

                  {filteredReviews[currentIndex].recommendNotes && (
                    <div className="recommend-notes">
                      <em>
                        "
                        {filteredReviews[currentIndex].recommendNotes}
                        "
                      </em>
                    </div>
                  )}

                  <div className="review-details">
                    {/* Safety & Environment */}
                    <div className="detail-section">
                      <h4>Safety & Environment</h4>
                      {renderRating(filteredReviews[currentIndex].safetyFeel, 'Safety')}
                      {renderRating(filteredReviews[currentIndex].areaCleanliness, 'Cleanliness')}
                      {renderRating(filteredReviews[currentIndex].trafficLevel, 'Traffic')}
                      {renderText(filteredReviews[currentIndex].noiseLevel, 'Noise')}
                      {renderYesNo(filteredReviews[currentIndex].swampAreaStreet, 'Street Swamp')}
                      {renderYesNo(filteredReviews[currentIndex].swampAreaYard, 'Yard Swamp')}
                    </div>

                    {/* Property Details */}
                    <div className="detail-section">
                      <h4>Property</h4>
                      {renderText(filteredReviews[currentIndex].kitchenQuality, 'Kitchen')}
                      {renderText(filteredReviews[currentIndex].airCirculation, 'Air Circulation')}
                      {renderYesNo(filteredReviews[currentIndex].hasElevator, 'Elevator')}
                      {filteredReviews[currentIndex].hasElevator === 'yes'
                        && renderText(filteredReviews[currentIndex].elevatorQuality, 'Elevator Quality')}
                    </div>

                    {/* Amenities */}
                    <div className="detail-section">
                      <h4>Amenities</h4>
                      <div className="amenity-item">
                        <span>
                          AC:
                          {filteredReviews[currentIndex].acInAllRooms === 'yes' ? '✅ All rooms' : '❌ Partial'}
                        </span>
                        {filteredReviews[currentIndex].acInAllRooms === 'no'
                          && <div className="notes">{filteredReviews[currentIndex].acNotes}</div>}
                      </div>
                      <div className="amenity-item">
                        <span>
                          Extractors:
                          {filteredReviews[currentIndex].extractorsInBathrooms === 'yes' ? '✅ All bathrooms' : '❌ Partial'}
                        </span>
                        {filteredReviews[currentIndex].extractorsInBathrooms === 'no'
                          && <div className="notes">{filteredReviews[currentIndex].extractorNotes}</div>}
                      </div>
                    </div>

                    {/* Landlord */}
                    {filteredReviews[currentIndex].landlordEthicsEmoji && (
                      <div className="detail-section">
                        <h4>Landlord</h4>
                        <div className="landlord-feedback">
                          <span className="emoji">{filteredReviews[currentIndex].landlordEthicsEmoji}</span>
                          <span className="notes">{filteredReviews[currentIndex].landlordEthics}</span>
                        </div>
                      </div>
                    )}

                    {/* Other Issues */}
                    {filteredReviews[currentIndex].otherProblems && (
                      <div className="detail-section">
                        <h4>Other Issues</h4>
                        <p>{filteredReviews[currentIndex].otherProblems}</p>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Edit Review Form Component
const EditReviewForm = ({ review, onSave, onCancel, onChange, saving }) => {
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    onChange({ ...review, [name]: value });
  };

  const handleRecommendChange = (value) => {
    onChange({ ...review, recommend: value });
  };

  return (
    <div className="edit-review-form">
      <div className="edit-header">
        <h4>✏️ Edit Review</h4>
        <div className="edit-actions">
          <button onClick={onCancel} disabled={saving} className="cancel-edit-btn">
            Cancel
          </button>
          <button onClick={onSave} disabled={saving} className="save-edit-btn">
            {saving ? 'Saving...' : '💾 Save Changes'}
          </button>
        </div>
      </div>

      <div className="edit-fields">
        <div className="edit-field">
          <label>Recommendation:</label>
          <div className="recommend-options">
            <button
              type="button"
              className={`recommend-btn ${review.recommend === 'yes' ? 'selected' : ''}`}
              onClick={() => handleRecommendChange('yes')}
            >
              ✅ Recommend
            </button>
            <button
              type="button"
              className={`recommend-btn ${review.recommend === 'no' ? 'selected' : ''}`}
              onClick={() => handleRecommendChange('no')}
            >
              ❌ Don't Recommend
            </button>
          </div>
        </div>

        <div className="edit-field">
          <label htmlFor="recommendNotes">Review Notes:</label>
          <textarea
            id="recommendNotes"
            name="recommendNotes"
            value={review.recommendNotes || ''}
            onChange={handleInputChange}
            placeholder="Why do you recommend or not recommend this place?"
            rows="3"
          />
        </div>

        <div className="edit-field">
          <label htmlFor="safetyFeel">Safety Rating (1-10):</label>
          <input
            type="number"
            id="safetyFeel"
            name="safetyFeel"
            min="1"
            max="10"
            value={review.safetyFeel || ''}
            onChange={handleInputChange}
          />
        </div>

        <div className="edit-field">
          <label htmlFor="areaCleanliness">Cleanliness Rating (1-10):</label>
          <input
            type="number"
            id="areaCleanliness"
            name="areaCleanliness"
            min="1"
            max="10"
            value={review.areaCleanliness || ''}
            onChange={handleInputChange}
          />
        </div>

        <div className="edit-field">
          <label htmlFor="otherProblems">Other Issues:</label>
          <textarea
            id="otherProblems"
            name="otherProblems"
            value={review.otherProblems || ''}
            onChange={handleInputChange}
            placeholder="Any other problems or positive aspects?"
            rows="2"
          />
        </div>
      </div>
    </div>
  );
};

export default EnhancedReviewList;
